import { Settings, Shield, User, Building2, Info } from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store';
import { usePermissions } from '../../hooks/usePermissions';
import { TrustedDevicePolicyCard } from '../../components/admin/TrustedDevicePolicyCard';

export function SettingsAdmin() {
  const { user, customer } = useAuthStore();
  const permissions = usePermissions();

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Settings</h1>
        <p className="text-slate-600 mt-1">System settings and information</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Platform security policy - the API is GlobalAdmin-only, so don't even try otherwise */}
        {permissions.isGlobalAdmin && <TrustedDevicePolicyCard />}

        {/* Current User Info */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <User size={20} className="text-blue-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800">Current User</h2>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-slate-100">
              <span className="text-slate-500">Name</span>
              <span className="text-slate-800 font-medium">
                {user?.firstName} {user?.lastName}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-100">
              <span className="text-slate-500">Email</span>
              <span className="text-slate-800 font-medium">{user?.email}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-slate-500">User ID</span>
              <span className="text-slate-800 font-medium text-sm font-mono">{user?.id}</span>
            </div>
          </div>
        </div>

        {/* Current Customer */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Building2 size={20} className="text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800">Current Customer</h2>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-slate-100">
              <span className="text-slate-500">Name</span>
              <span className="text-slate-800 font-medium">{customer?.name}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-slate-500">Customer ID</span>
              <span className="text-slate-800 font-medium text-sm font-mono">{customer?.id}</span>
            </div>
          </div>
        </div>

        {/* User Roles */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Shield size={20} className="text-purple-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800">Your Roles</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {user?.roles && user.roles.length > 0 ? (
              user.roles.map((role) => (
                <span
                  key={role}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                    role === 'GlobalAdmin'
                      ? 'bg-purple-100 text-purple-700'
                      : role === 'UserAdmin'
                      ? 'bg-blue-100 text-blue-700'
                      : role === 'LicenseAdmin'
                      ? 'bg-green-100 text-green-700'
                      : role === 'CustomerAdmin'
                      ? 'bg-orange-100 text-orange-700'
                      : 'bg-slate-100 text-slate-700'
                  }`}
                >
                  {role}
                </span>
              ))
            ) : (
              <span className="text-slate-500">No roles assigned</span>
            )}
          </div>
        </div>

        {/* Permissions Summary */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Settings size={20} className="text-orange-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800">Your Permissions</h2>
          </div>
          <div className="space-y-2">
            <PermissionItem
              label="Manage All Customers"
              enabled={permissions.canManageAllCustomers}
            />
            <PermissionItem
              label="Manage Global Roles"
              enabled={permissions.canManageGlobalRoles}
            />
            <PermissionItem
              label="Manage Licenses"
              enabled={permissions.canManageLicenses}
            />
            <PermissionItem
              label="Manage Customer Users"
              enabled={permissions.canManageCustomerUsers}
            />
          </div>
        </div>

        {/* System Info */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 lg:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <Info size={20} className="text-slate-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800">System Information</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-500 block">Portal Version</span>
              <span className="text-lg font-semibold text-slate-800">0.0.1</span>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-500 block">Environment</span>
              <span className="text-lg font-semibold text-slate-800">Development</span>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-500 block">API Status</span>
              <span className="text-lg font-semibold text-green-600">Connected</span>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-500 block">Session</span>
              <span className="text-lg font-semibold text-slate-800">Active</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PermissionItem({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-slate-600">{label}</span>
      <span
        className={`px-2 py-0.5 rounded text-xs font-medium ${
          enabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
        }`}
      >
        {enabled ? 'Enabled' : 'Disabled'}
      </span>
    </div>
  );
}

export default SettingsAdmin;
